// komponent wise-tile - kafelek z kluczowymi wskaźnikami

import React, { FC, useMemo } from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import * as BootstrapIcons from 'react-bootstrap-icons';
import { Paper, Skeleton, Tooltip, LinearProgress, Box } from '@mui/material';
import { InfoCircle } from 'react-bootstrap-icons';

import styles from 'theme/components/wise/WiseTile/WiseTile.module.scss';

interface IIconConfig {
  type: string;
  url: string;
}

interface IWiseTileTextProps {
  type: 'text';
  icon: IIconConfig;
  title: string;
  data: {
    text: string;
    textAdditional?: string;
  };
  isLoading?: boolean;
  info?: string;
  href?: string;
}

interface IWiseTileRatioProps {
  type: 'ratio';
  icon: IIconConfig;
  title: string;
  data: {
    target: number;
    current: number;
  };
  isLoading?: boolean;
  info?: string;
  href?: string;
}

type IWiseTileProps = IWiseTileTextProps | IWiseTileRatioProps;

const WiseTile: FC<IWiseTileProps> = ({
  icon,
  title,
  type,
  data,
  isLoading = false,
  info,
  href
}) => {
  const renderIcon = useMemo(() => {
    // Jeśli URL jest podany, użyj obrazka
    if (icon.url && icon.url.trim() !== '') {
      return <img src={icon.url} alt={icon.type} className={styles.iconImage} />;
    }

    // W przeciwnym razie użyj ikony z bootstrap-icons
    const IconComponent = (BootstrapIcons as any)[icon.type] as React.ComponentType<any>;
    if (IconComponent) {
      return <IconComponent className={styles.iconSvg} />;
    }

    // Fallback - jeśli ikona nie została znaleziona
    return <div className={styles.iconPlaceholder}>{icon.type}</div>;
  }, [icon]);

  const renderData = () => {
    if (type === 'ratio') {
      return `${data.current}/${data.target}`;
    }
    return data.text;
  };

  const calculateProgress = () => {
    if (type === 'ratio') {
      const percentage = (data.current / data.target) * 100;
      return Math.min(Math.max(percentage, 0), 100); // Ograniczenie do 0-100%
    }
    return 0;
  };


  const paperClassName = classnames(
    styles.componentWrapper,
    'StylePath-Components-Wise-WiseTile',
    href && styles.clickable
  );

  const paperContent = (
    <>
      <div className={styles.iconWrapper}>{renderIcon}</div>
      <div className={styles.content}>
        <div className={styles.title}>{title}</div>
        <div className={styles.dataWrapper}>
          <div className={styles.data}>{renderData()}</div>
          {type === 'text' && data.textAdditional && (
            <div className={styles.dataAdditional}>{data.textAdditional}</div>
          )}
        </div>
      </div>
      <div className={styles.contentAdditional}>
        {type === 'ratio' && (
          <Box className={styles.progressWrapper}>
            <LinearProgress
              variant="determinate"
              value={calculateProgress()}
              className={styles.progressBar}
            />
            <Box className={styles.progressLabel}>
              {Math.round(calculateProgress())}%
            </Box>
          </Box>
        )}
        {info && (
          <Tooltip title={info} arrow placement="top">
            <div className={styles.infoIcon}>
              <InfoCircle />
            </div>
          </Tooltip>
        )}
      </div>
    </>
  );

  if (isLoading) {
    return (
      <Paper
        className={classnames(styles.componentWrapper, 'StylePath-Components-Wise-WiseTile')}
        elevation={0}>
        <Skeleton variant="rectangular" className={styles.iconWrapper} width={40} height={40} />
        <div className={styles.content}>
          <Skeleton variant="text" width="60%" height={20} className={styles.title} />
          <Skeleton variant="text" width="40%" height={32} className={styles.data} />
        </div>
      </Paper>
    );
  }

  if (href) {
    return (
      <Link to={href} className={styles.tileLink}>
        <Paper className={paperClassName}>{paperContent}</Paper>
      </Link>
    );
  }

  return <Paper className={paperClassName}>{paperContent}</Paper>;
};

export default WiseTile;
